import { useState } from 'react'
import type { ApiToken, ApiTokenCreated, ApiTokenScope } from '@workflow/contracts'
import { API_BASE } from '@workflow/contracts'
import { ActionMenu, Button, Checkbox, Dropdown, EmptyNote, Field, Icons, Loading, StateNote, StatusLabel, TextField, notify } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useApiTokens, useCreateApiToken, useDeleteApiToken, useRevokeApiToken, useSession } from '@/lib/api/hooks'
import { formatTimestamp } from '@/lib/format'

/**
 * The machine passport, administered: the base URL anything points at, the
 * keys that open it, and the life of each key on the record. A key acts as
 * the administrator who cut it, capped by its scopes; the secret is shown
 * exactly once — after that, only the prefix exists anywhere.
 */

const SCOPES: readonly { scope: ApiTokenScope; name: string; detail: string }[] = [
  { scope: 'read', name: 'Read', detail: 'See the work — every list and record.' },
  { scope: 'write', name: 'Write', detail: 'Shape the work — create, edit, assign, complete.' },
  { scope: 'admin', name: 'Admin', detail: 'Run the studio — members, audit, the tokens themselves never.' },
]

export function ApiPane() {
  const session = useSession()
  const isAdmin = session.data?.account?.role === 'admin'
  const tokens = useApiTokens()
  const [cutting, setCutting] = useState(false)
  const [created, setCreated] = useState<ApiTokenCreated | null>(null)

  if (!isAdmin) {
    return (
      <StateNote
        title="This part of settings is for administrators."
        detail="API tokens are created by administrators. A token acts as the administrator who created it."
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {created ? (
        <ShowOnce token={created} onDone={() => setCreated(null)} />
      ) : cutting ? (
        <CutForm onDone={(token) => { setCutting(false); setCreated(token) }} onCancel={() => setCutting(false)} />
      ) : (
        <div>
          <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setCutting(true)}>
            Create token
          </Button>
        </div>
      )}

      <div>
        <h3 className="text-micro text-muted-foreground uppercase">Base URL</h3>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <code className="min-w-0 break-all font-mono text-caption text-foreground">{window.location.origin}{API_BASE}</code>
          <Button size="xs" variant="ghost" icon={<Icons.copy />} onClick={async () => {
            try {
              await navigator.clipboard.writeText(`${window.location.origin}${API_BASE}`)
              notify({ tone: 'success', title: 'Base URL copied.' })
            } catch {
              notify({ tone: 'danger', title: 'Could not copy the base URL.' })
            }
          }}>Copy</Button>
        </div>
        <p className="text-caption text-muted-foreground">Send your key as <code>Authorization: Bearer wf_…</code>. GET the base URL for the route list.</p>
      </div>

      <div>
        <h3 className="text-micro text-muted-foreground uppercase">The keys</h3>
        <div className="mt-1.5">
          {tokens.isLoading ? (
            <Loading label="Loading tokens" />
          ) : tokens.error ? (
            <StateNote title="The API tokens could not be loaded." detail={describeError(tokens.error)} />
          ) : (tokens.data?.length ?? 0) === 0 ? (
            <EmptyNote title="No API tokens yet." detail="Create one when an integration needs API access." />
          ) : (
            <ul className="flex flex-col">
              {tokens.data!.map((token) => (
                <TokenRow key={token.id} token={token} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

/** The creation moment: the secret, in full, for the last time. */
function ShowOnce({ token, onDone }: { token: ApiTokenCreated; onDone: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex flex-col gap-5 rounded-2xl border bg-surface px-5 py-5 shadow-chip">
      <p className="text-ui font-medium text-foreground">Token created: {token.name}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md border bg-surface-sunken px-2 py-1.5 font-mono text-[13px] text-foreground">
          {token.token}
        </code>
        <Button
          size="xs"
          variant="ghost"
          icon={<Icons.copy />}
          onClick={() => {
            void navigator.clipboard.writeText(token.token)
            setCopied(true)
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="mt-2 text-caption text-danger">
        Copy it now. This is the only time the secret is shown — the record keeps only its prefix.
      </p>
      {token.expiresAt ? (
        <p className="mt-1 text-caption text-muted-foreground">
          The token stops working on its own on {formatTimestamp(token.expiresAt)} — create a new one then, or revoke it
          earlier.
        </p>
      ) : null}
      <div className="mt-3">
        <Button size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  )
}

const LIFESPANS: readonly { label: string; days: number | null }[] = [
  { label: 'Until revoked', days: null },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
]

function CutForm({ onDone, onCancel }: { onDone: (token: ApiTokenCreated) => void; onCancel: () => void }) {
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<ApiTokenScope[]>(['read'])
  const [days, setDays] = useState<number | null>(null)
  const create = useCreateApiToken()

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    create.mutate(
      { name: name.trim(), scopes, ...(days === null ? {} : { expiresInDays: days }) },
      { onSuccess: (data) => onDone(data as ApiTokenCreated) },
    )
  }

  const toggle = (scope: ApiTokenScope) =>
    setScopes((current) => (current.includes(scope) ? current.filter((entry) => entry !== scope) : [...current, scope]))

  return (
    <form onSubmit={submit} className="flex flex-col gap-5 rounded-2xl border bg-surface px-5 py-5 shadow-chip">
      <Field label="Name" htmlFor="token-name" hint="What the token is for — it will be named in the audit trail.">
        <TextField
          id="token-name"
          value={name}
          autoFocus
          placeholder="e.g. MCP — Claude"
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Field label="Lifespan" htmlFor="token-lifespan" hint="After this day the token stops working automatically.">
        <Dropdown
          id="token-lifespan"
          ariaLabel="Lifespan"
          value={days === null ? 'never' : String(days)}
          options={LIFESPANS.map((entry) => ({ value: entry.days === null ? 'never' : String(entry.days), label: entry.label }))}
          onChange={(value) => setDays(value === 'never' ? null : Number(value))}
        />
      </Field>
      <fieldset className="min-w-0">
        <legend className="text-micro text-muted-foreground uppercase">Scopes</legend>
        <div className="mt-2 flex flex-col gap-1.5">
          {SCOPES.map((entry) => (
            <Checkbox
              key={entry.scope}
              className="rounded-lg px-2 py-2 hover:bg-tint"
              checked={scopes.includes(entry.scope)}
              onChange={() => toggle(entry.scope)}
            >
              <span className="block text-ui font-medium text-foreground">{entry.name}</span>
              <span className="block text-caption text-muted-foreground">{entry.detail}</span>
            </Checkbox>
          ))}
        </div>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" variant="primary" busy={create.isPending} disabled={!name.trim() || scopes.length === 0}>
          Create token
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        {create.error ? <p className="text-caption text-danger">{describeError(create.error)}</p> : null}
      </div>
    </form>
  )
}

function TokenRow({ token }: { token: ApiToken }) {
  const revoke = useRevokeApiToken()
  const remove = useDeleteApiToken()
  const revoked = token.revokedAt !== null
  const expired = token.expiresAt !== null && Date.parse(token.expiresAt) <= Date.now()
  const status = revoked ? 'Revoked' : expired ? 'Expired' : 'Active'
  const busy = revoke.isPending || remove.isPending
  const error = revoke.error ?? remove.error

  return (
    <li className="border-b border-hairline py-3 last:border-0">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-start gap-x-3 gap-y-1">
        <div className="min-w-0">
          <span className="block truncate text-ui font-medium text-foreground">{token.name}</span>
          <span className="block truncate font-mono text-caption text-muted-foreground">{token.prefix}… · {token.scopes.join(', ')}</span>
        </div>
        <StatusLabel tone={status === 'Active' ? 'done' : expired && !revoked ? 'attention' : 'idle'} label={status} />
        <ActionMenu label={`Actions for ${token.name}`} sections={[{ items: [
          {
            key: 'revoke', label: 'Revoke', danger: true, disabled: busy || revoked,
            ask: { title: `Revoke ${token.name}?`, detail: 'Integrations using this key will lose access immediately.', confirmLabel: 'Revoke' },
            onSelect: () => revoke.mutate({ id: token.id }),
          },
          {
            key: 'delete', label: 'Delete Permanently', danger: true, disabled: busy || !revoked,
            ask: { title: `Delete ${token.name} permanently?`, detail: 'The revoked key will be removed from this list. Its audit history is retained.', confirmLabel: 'Delete Permanently' },
            onSelect: () => remove.mutate(token.id),
          },
        ] }]} />
        <p className="col-span-full text-caption text-muted-foreground">
          {token.lastUsedAt ? `Last used ${formatTimestamp(token.lastUsedAt)}` : 'Never used'}
          {revoked ? ` · Revoked ${formatTimestamp(token.revokedAt!)}` : token.expiresAt ? ` · ${expired ? 'Expired' : 'Expires'} ${formatTimestamp(token.expiresAt)}` : ''}
        </p>
        {error ? <p role="alert" className="col-span-full text-caption text-danger">{describeError(error)}</p> : null}
      </div>
    </li>
  )
}
